/**
 * Deal Room Activity Heatmap - 7-day engagement grid per investor.
 */
import React, { useMemo } from 'react';
import { DealRoomActivity } from '../types/dealRoom';

interface HeatmapProps {
  activities: DealRoomActivity[];
  days?: number;
}

const dayStartUtc = (d: Date): Date => {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
};

const intensityClass = (count: number): string => {
  if (count <= 0) return 'bg-gray-100';
  if (count < 2) return 'bg-blue-100';
  if (count < 5) return 'bg-blue-300';
  if (count < 10) return 'bg-blue-500';
  return 'bg-blue-700';
};

const DealRoomActivityHeatmap: React.FC<HeatmapProps> = ({
  activities,
  days = 7,
}) => {
  const { dayLabels, grid, investorLabels } = useMemo(() => {
    const now = dayStartUtc(new Date());
    const dayList: Date[] = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      dayList.push(d);
    }
    const dayLabels = dayList.map((d) =>
      d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    );

    // Group by investor
    const byInvestor: Record<number, { name: string; counts: number[] }> = {};
    activities.forEach((a) => {
      const investorId = a.investor_id;
      if (!byInvestor[investorId]) {
        byInvestor[investorId] = {
          name:
            a.investor?.full_name ||
            a.investor?.firm_name ||
            `Investor #${investorId}`,
          counts: new Array(days).fill(0),
        };
      }
      const created = new Date(a.created_at);
      const dstart = dayStartUtc(created);
      const dayIdx = dayList.findIndex(
        (d) => d.getTime() === dstart.getTime()
      );
      if (dayIdx >= 0) {
        byInvestor[investorId].counts[dayIdx] += 1;
      }
    });

    const investorIds = Object.keys(byInvestor).map((k) => Number(k));
    const investorLabels = investorIds.map((id) => byInvestor[id].name);
    const grid = investorIds.map((id) => byInvestor[id].counts);

    return { dayLabels, grid, investorLabels };
  }, [activities, days]);

  if (grid.length === 0) {
    return (
      <div className="text-sm text-gray-500 py-6 text-center">
        No recent activity to visualize.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-separate" style={{ borderSpacing: 2 }}>
        <thead>
          <tr>
            <th className="text-left text-xs text-gray-500 font-medium pr-3 pb-2">
              Investor
            </th>
            {dayLabels.map((label) => (
              <th
                key={label}
                className="text-center text-[10px] text-gray-500 font-medium pb-2 min-w-[48px]"
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.map((row, rIdx) => (
            <tr key={rIdx}>
              <td className="text-xs text-gray-700 pr-3 whitespace-nowrap max-w-[180px] truncate">
                {investorLabels[rIdx]}
              </td>
              {row.map((count, cIdx) => (
                <td key={cIdx} className="text-center">
                  <div
                    title={`${count} event${count === 1 ? '' : 's'} on ${dayLabels[cIdx]}`}
                    className={`w-10 h-8 mx-auto rounded ${intensityClass(count)} flex items-center justify-center text-[10px] ${
                      count >= 5 ? 'text-white' : 'text-gray-700'
                    }`}
                  >
                    {count > 0 ? count : ''}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
        <span>Less</span>
        <span className="w-4 h-4 rounded bg-gray-100 border border-gray-200" />
        <span className="w-4 h-4 rounded bg-blue-100" />
        <span className="w-4 h-4 rounded bg-blue-300" />
        <span className="w-4 h-4 rounded bg-blue-500" />
        <span className="w-4 h-4 rounded bg-blue-700" />
        <span>More</span>
      </div>
    </div>
  );
};

export default DealRoomActivityHeatmap;
